// src/components/FormPage.jsx
import React, { useLayoutEffect, useMemo, useRef, useState, useCallback, useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import FormWrapper from './FormWrapper';

import IntroStep from './new/forms/IntroStep';
import ImpressionsForm from './new/forms/ImpressionsForm';
import ContentForm from './new/forms/ContentForm';
// import CreativeLabourForm from './new/forms/CreativeLabourForm';
import ScrollTimeForm from './new/forms/ScrollTimeForm';
import ValueForm from './new/forms/ValueForm';
import ShareForm from './new/forms/ShareForm';
import WellbeingForm from './new/forms/WellbeingForm';
import InvoiceForm from './new/forms/InvoiceForm';
import CreativeLabourForm from './new/forms/CreativeLabourForm';
import { ArrowDownward } from '@mui/icons-material';
// import { useAutoScrollTopOnKeyboardClose } from "../hooks/useAutoScrollTopOnKeyboardClose";

// const steps = ['intro', 'labour', 'reach', 'value', 'share', 'invoice'];
const steps = ['intro', 'impressions', 'content', 'scroll-time', 'labour', 'value',  'wellbeing', 'invoice'];

export default function FormPage() {
  const viewportRef = useRef(null);
  const sectionRefs = useMemo(() => steps.map(() => React.createRef()), []);
  const formRefs = useMemo(() => steps.map(() => React.createRef()), []);

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const currentSlug = pathname.split('/')[2] || steps[0];
  const index = Math.max(0, steps.indexOf(currentSlug));

  // useAutoScrollTopOnKeyboardClose();

  // Scroll to current section whenever the URL changes
  useLayoutEffect(() => {
    const el = sectionRefs[index]?.current;
    const viewport = viewportRef.current;
    if (!el || !viewport) return;
    const top = el.offsetTop - viewport.offsetTop;
    requestAnimationFrame(() => viewport.scrollTo({ top, behavior: 'smooth' }));
  }, [index, sectionRefs]);

  const [busy, setBusy] = useState(false);

  const go = useCallback(
    (i) => {
      if (i < 0 || i >= steps.length) return;
      navigate(`/form/${steps[i]}`);
    },
    [navigate]
  );

  const resnapTo = useCallback((i) => {
    const vp = viewportRef.current;
    const el = sectionRefs[i]?.current;
    if (vp && el) {
      const top = el.offsetTop - vp.offsetTop;
      vp.scrollTo({ top, behavior: 'smooth' });
    }
  }, [sectionRefs]);

  const handleBack = useCallback(() => {
    if (busy) return;
    if (index === 0) {
      navigate('/'); // Home from intro
      return;
    }
    go(index - 1);
  }, [busy, index, navigate, go]);

  const handleNext = useCallback(async () => {
    if (busy) return;
    const api = formRefs[index]?.current;
    if (api?.validateAndSave) {
      setBusy(true);
      const ok = await api.validateAndSave();
      setBusy(false);
      if (!ok) return;
    }
    if (index < steps.length - 1) go(index + 1);
    else navigate('/review');
  

  }, [busy, formRefs, index, go, navigate]);



  // Wheel/swipe interception (original behavior you reverted to)
  const isTransitioning = useRef(false);
  const touchStartY = useRef(null);

  const pageTo = (delta) => {
    if (isTransitioning.current || busy) return;
    const nextIndex = delta > 0 ? index + 1 : index - 1;

    if (nextIndex < 0 || nextIndex >= steps.length) {
      resnapTo(index);
      return;
    }

    isTransitioning.current = true;
    if (delta > 0) {
      handleNext();
    } else {
      go(nextIndex);
    }
    setTimeout(() => {
      isTransitioning.current = false;
    }, 500);
  };

  const onWheel = (e) => {
    const threshold = 10;
    if (Math.abs(e.deltaY) < threshold) return;
    e.preventDefault();
    pageTo(e.deltaY);
  };

  const onTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY;
  };

  const onTouchMove = (e) => {
    e.preventDefault();
  };

  const onTouchEnd = (e) => {
    const start = touchStartY.current;
    if (start == null) return;
    const dy = e.changedTouches[0].clientY - start;
    const threshold = 50;
    if (Math.abs(dy) >= threshold) pageTo(-dy);
    touchStartY.current = null;
  };

  // NEW: clicking a progress “pill” navigates to that step
  const handleStepClick = useCallback(
    (i) => {
      if (i === index) return;
      if (i < 0 || i >= steps.length) return;
      navigate(`/form/${steps[i]}`);
    },
    [index, navigate]
  );

  return (
    <FormWrapper
      /* added props so header trackers become links */
      steps={steps}
      currentIndex={index}
      onStepClick={handleStepClick}
      /* existing props */
      onBack={handleBack}
      onNext={handleNext}
      backDisabled={busy}
      nextDisabled={busy}
      nextLabel={index === steps.length - 1 ? 'Generate' : 'Next'}
      backLabel={index === 0 ? 'Home' : 'Back'}
    >
      <div
        ref={viewportRef}
        className="steps-viewport"
        onWheel={onWheel}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        tabIndex={0}
        role="region"
        aria-label="Multi-step form"
        >

        <section ref={sectionRefs[0]} className="step-section" id="intro">
          <IntroStep ref={formRefs[0]} />
        </section>
        <section ref={sectionRefs[1]} className="step-section" id="impressions">
          <ImpressionsForm ref={formRefs[1]} />
        </section>
        <section ref={sectionRefs[2]} className="step-section" id="content">
          <ContentForm ref={formRefs[2]} />
        </section>
        <section ref={sectionRefs[3]} className="step-section" id="scroll-time">
          <ScrollTimeForm ref={formRefs[3]} />
        </section>
        <section ref={sectionRefs[4]} className="step-section" id="labour">
          <CreativeLabourForm ref={formRefs[4]} />
        </section>
        <section ref={sectionRefs[5]} className="step-section" id="value">
          <ValueForm ref={formRefs[5]} />
        </section>
        <section ref={sectionRefs[6]} className="step-section" id="wellbeing">
          <WellbeingForm ref={formRefs[6]} />
        </section>
        <section ref={sectionRefs[7]} className="step-section" id="invoice">
          <InvoiceForm ref={formRefs[7]} />
        </section>

        <Routes>
          <Route index element={<Navigate to={steps[0]} replace />} />
          {steps.map((s) => (
            <Route key={s} path={s} element={<></>} />
          ))}
          <Route path="*" element={<Navigate to={steps[0]} replace />} />
        </Routes>
      </div>
      
      {
        pathname != '/form/invoice' && 

        <div style={{position: 'absolute', right: 10, bottom: 20}}>
          <button onClick={handleNext} style={{width: 40, height: 40, borderRadius: 20, opacity: 0.75, padding: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <ArrowDownward></ArrowDownward>
            
          </button>
        </div>
      }

    </FormWrapper>
  );
}
